/**
 * terminalLayoutStore — the DURABLE record of which terminal tabs the user has
 * intentionally open, per project, so they can be REBUILT after the PTYs are
 * truly gone (a machine reboot, or the pty-daemon being killed).
 *
 * This is distinct from, and complementary to, the two existing mechanisms:
 *   - `terminalStore` snapshot (localStorage `ateliercode:persisted-terminals`)
 *     + `adoptAllDaemonSessions` REATTACH to still-living PTYs by their id. That
 *     covers the common case (reload / clean quit / crash — the daemon survives).
 *   - This store covers the case those can't: the PTYs no longer exist, so there
 *     is nothing to reattach to and we must SPAWN fresh sessions that re-launch
 *     the same agents, in the same projects, as before.
 *
 * Contract (what the user asked for):
 *   - Closing a tab by hand FORGETS it (removeByPty on the manual-close path) —
 *     it must never be resurrected.
 *   - Everything else stays: a session that merely vanished because the daemon
 *     died is NOT pruned here (the init-sync "remove stale" path deliberately
 *     does not touch this store), so it gets recreated on next open.
 *
 * Each entry carries the relaunch recipe (the exact launch command, agent key,
 * kind, shell) plus `currentPtyId`, the live PTY it's bound to right now. The
 * binding is only used to prune the right entry on manual close and to keep the
 * record converged with reality; it is irrelevant across a reboot (every PTY is
 * gone and recreate rebinds each entry to a fresh id).
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface TerminalLayoutEntry {
  /** Stable identity for this tab across restarts (PTY ids are not stable). */
  layoutId: string;
  /** The live PTY this entry is bound to right now, or null when not running
   *  (e.g. just after a reboot, before recreate rebinds it). Used to prune the
   *  correct entry when the user closes that tab by hand. */
  currentPtyId: number | null;
  title: string;
  isAgent: boolean;
  /** Agent CLI key (AGENT_CLI_CONFIG) when this was an agent tab. */
  agentType?: string;
  /** What the tab was: 'agent' | 'terminal' | 'run'. 'auth' is never recorded. */
  kind?: string;
  /** Exact command auto-launched in the session — the relaunch recipe. null for
   *  a plain shell (recreate just opens a named shell). */
  launchCmd?: string | null;
  /** Run-config name when kind === 'run'. */
  runConfig?: string;
  /** Shell the session was spawned with (so recreate uses the same one). */
  shell?: string;
}

interface TerminalLayoutState {
  /** projectPath → the tabs that should be open for it. */
  layouts: Record<string, TerminalLayoutEntry[]>;

  /** Record a brand-new tab the user (or a restore) just opened. Returns its
   *  layoutId. `currentPtyId` binds it to the live PTY. */
  recordCreate: (projectPath: string, entry: Omit<TerminalLayoutEntry, 'layoutId'> & { layoutId?: string }) => string;
  /** Point an existing entry at a (new) live PTY — used after a restore respawns
   *  it with a fresh id, so manual-close pruning still finds it. */
  bindPty: (projectPath: string, layoutId: string, ptyId: number | null) => void;
  /** Converge the record with a living daemon session: refresh the entry already
   *  bound to this PTY, or add one if none exists yet (covers sessions created
   *  before this store existed, or in a previous app run). */
  upsertLiving: (
    projectPath: string,
    session: {
      ptyId: number;
      title: string;
      isAgent: boolean;
      agentType?: string;
      kind?: string;
      launchCmd?: string | null;
      runConfig?: string;
      shell?: string;
    },
  ) => void;
  /** Manual close: forget the entry bound to this PTY so it's never recreated. */
  removeByPty: (projectPath: string, ptyId: number) => void;
  /** The tabs that should be open for a project (for recreate). */
  getEntries: (projectPath: string) => TerminalLayoutEntry[];
}

function newId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `lay-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }
}

export const useTerminalLayoutStore = create<TerminalLayoutState>()(
  persist(
    (set, get) => ({
      layouts: {},

      recordCreate: (projectPath, entry) => {
        // 'auth' sign-in flows are one-shot; never remember them for recreate.
        if (entry.kind === 'auth') return entry.layoutId ?? '';
        const layoutId = entry.layoutId ?? newId();
        set((state) => {
          const list = state.layouts[projectPath] ?? [];
          // Dedupe by the PTY we're binding (a re-record of the same live tab
          // replaces rather than duplicates).
          const withoutPty =
            entry.currentPtyId != null
              ? list.filter((e) => e.currentPtyId !== entry.currentPtyId)
              : list;
          const next = withoutPty.filter((e) => e.layoutId !== layoutId);
          next.push({ ...entry, layoutId });
          return { layouts: { ...state.layouts, [projectPath]: next } };
        });
        return layoutId;
      },

      bindPty: (projectPath, layoutId, ptyId) =>
        set((state) => {
          const list = state.layouts[projectPath];
          if (!list) return state;
          return {
            layouts: {
              ...state.layouts,
              [projectPath]: list.map((e) =>
                e.layoutId === layoutId ? { ...e, currentPtyId: ptyId } : e,
              ),
            },
          };
        }),

      upsertLiving: (projectPath, session) => {
        if (session.kind === 'auth') return;
        set((state) => {
          const list = state.layouts[projectPath] ?? [];
          const existing = list.find((e) => e.currentPtyId === session.ptyId);
          if (existing) {
            // Refresh metadata (e.g. a title that self-healed to the agent name).
            return {
              layouts: {
                ...state.layouts,
                [projectPath]: list.map((e) =>
                  e.currentPtyId === session.ptyId
                    ? {
                        ...e,
                        title: session.title,
                        isAgent: session.isAgent,
                        agentType: session.agentType ?? e.agentType,
                        kind: session.kind ?? e.kind,
                        launchCmd: session.launchCmd ?? e.launchCmd,
                        runConfig: session.runConfig ?? e.runConfig,
                        shell: session.shell ?? e.shell,
                      }
                    : e,
                ),
              },
            };
          }
          // No entry yet for this live session — adopt it into the record.
          const entry: TerminalLayoutEntry = {
            layoutId: newId(),
            currentPtyId: session.ptyId,
            title: session.title,
            isAgent: session.isAgent,
            agentType: session.agentType,
            kind: session.kind,
            launchCmd: session.launchCmd,
            runConfig: session.runConfig,
            shell: session.shell,
          };
          return { layouts: { ...state.layouts, [projectPath]: [...list, entry] } };
        });
      },

      removeByPty: (projectPath, ptyId) =>
        set((state) => {
          const list = state.layouts[projectPath];
          if (!list) return state;
          const next = list.filter((e) => e.currentPtyId !== ptyId);
          if (next.length === list.length) return state;
          return { layouts: { ...state.layouts, [projectPath]: next } };
        }),

      getEntries: (projectPath) => get().layouts[projectPath] ?? [],
    }),
    { name: 'ateliercode:terminal-layout' },
  ),
);
